import { Component, computed, inject, input, model, signal } from '@angular/core';
import { isDefined, ModalClass, TextModel, TextModelOptions, SmartlockerDeliveryStatus } from '@evorait/pace-core';
import { SmartlockerData, SmartlockerDoorMaterials } from '../../../interfaces/smartlocker-doors.interface';
import { SmartlockerAccessCodeComponent } from '../../smartlocker-access-code/smartlocker-access-code.component';
import { BsModalService } from 'ngx-bootstrap/modal';
import { catchError, of, take } from 'rxjs';
import { LogInstanceToken } from '@evorait/pace-logging';
import {
    SmartlockerMaterial,
    SmartLockerMaterialWithAccessCode,
} from '../../../interfaces/smartlocker-deliveries.interface';
import { TranslateService } from '@ngx-translate/core';

@Component({
    selector: 'pace-smartlocker-pickup-details',
    templateUrl: './smartlocker-pickup-details.component.html',
    standalone: false,
})
export class SmartlockerPickupDetailsComponent {
    public doorData = input.required<SmartlockerDoorMaterials>();
    public smartlockerData = input.required<SmartlockerData>();

    public pickedUpMaterials = model.required<SmartLockerMaterialWithAccessCode[]>();
    // used for displaying the picked-up materials for the current door.
    public pickedUpMaterialsForDoor = computed<SmartlockerMaterial[]>(() =>
        this.pickedUpMaterials().filter((pickedUpModel) => pickedUpModel.SmartlockerDoorId === this.doorData().doorId),
    );

    public displayError = signal<string>('');
    public ilpnInputModel: TextModel = this.getIlPnInputModel();
    public smartLockerAccessCode = '';
    public doorDetails = computed(() => {
        const doorData = this.doorData().doorData;
        return doorData ? `${doorData.DoorIdentifier} - ${doorData.Name}` : '';
    });

    private readonly log = inject(LogInstanceToken);
    private readonly modalService = inject(BsModalService);
    private readonly translate = inject(TranslateService);

    public getSmartlockerAccessCode(): void {
        const modalInstance = this.modalService.show(SmartlockerAccessCodeComponent, {
            backdrop: 'static',
            class: ModalClass.MODAL_MEDIUM,
            initialState: {
                smartlockerId: this.smartlockerData().SmartlockerId,
                smartlockerDoorId: this.doorData().doorId,
            },
            ariaLabelledBy: 'smartlocker-access-code-title',
        });

        modalInstance.content?.modalResult
            ?.pipe(
                take(1),
                catchError((error) => {
                    this.log.error(
                        `SmartlockerPickupDetailsComponent: getSmartlockerAccessCode: modalResult: Error while getting the result of the Access Code modal: ${this.smartlockerData().SmartlockerId} for door ${this.doorData().doorId}`,
                        error,
                    );
                    return of('');
                }),
            )
            .subscribe((result) => {
                if (result.length > 0) {
                    this.smartLockerAccessCode = result;
                }
            });
    }

    public checkValidity(): void {
        const inputtedValue = this.ilpnInputModel.value;

        if (inputtedValue.length === 0) {
            this.displayError.update(() => '');
            return;
        }

        const foundSmartLockerMaterialInDoor = this.doorData().materials.find(
            (material) =>
                material.ILPN === this.ilpnInputModel.value &&
                (material.Status === SmartlockerDeliveryStatus.InTransit ||
                    material.Status === SmartlockerDeliveryStatus.Sorted),
        );

        if (!isDefined(foundSmartLockerMaterialInDoor)) {
            const errorMessage = <string>this.translate.instant('SMARTLOCKER_PICKUP_MATERIAL_NOT_FOUND_IN_DOOR', {
                ilpn: this.ilpnInputModel.value,
                doorId: this.doorData().doorId,
            });

            this.displayError.update(() => errorMessage);
            return;
        }

        const alreadyPickedSmartLockerMaterial = this.pickedUpMaterials().find(
            (pickedUpMaterial) => pickedUpMaterial.ILPN === foundSmartLockerMaterialInDoor.ILPN,
        );

        if (isDefined(alreadyPickedSmartLockerMaterial)) {
            this.displayError.update(() => <string>this.translate.instant('SMARTLOCKER_ILPN_ALREADY_ENTERED'));
            return;
        }

        this.displayError.update(() => '');
    }

    public addMaterial(): void {
        const foundSmartLockerMaterialInDoor = this.doorData().materials.find(
            (material) => material.ILPN === this.ilpnInputModel.value,
        );

        if (!isDefined(foundSmartLockerMaterialInDoor)) {
            this.log.error(
                `SmartlockerPickupDetailsComponent: addMaterial: it should not be possible to reach here, as the checkValidity method is checking the validity of the data`,
            );
            return;
        }

        this.ilpnInputModel = this.getIlPnInputModel();

        const newSmartLockerMaterialWithAccessCode: SmartLockerMaterialWithAccessCode = {
            ...foundSmartLockerMaterialInDoor,
            AccessCode: this.smartLockerAccessCode,
        };
        this.pickedUpMaterials.update((prev) => [...prev, newSmartLockerMaterialWithAccessCode]);
    }

    public removeMaterial(material: SmartlockerMaterial): void {
        const filteredMaterials = this.pickedUpMaterials().filter(
            (pickedUpMaterial) => pickedUpMaterial.ILPN !== material.ILPN,
        );

        this.pickedUpMaterials.update(() => filteredMaterials);
    }

    private getIlPnInputModel(): TextModel {
        return new TextModel('', '', new TextModelOptions({ scannable: true }));
    }
}
